import { apiClient } from './request';
import { create, fromJson, type JsonValue, toJson } from '@bufbuild/protobuf';
import {
  type Sandbox,
  SandboxSchema,
  type SandboxClass,
  SandboxClassSchema,
} from '@/proto/agentplatform/v1/sandbox_pb';
import {
  ListSandboxClassesResponseSchema,
  CreateSandboxClassRequestSchema,
  CreateSandboxClassResponseSchema,
  GetSandboxClassRequestSchema,
  GetSandboxClassResponseSchema,
  UpdateSandboxClassRequestSchema,
  UpdateSandboxClassResponseSchema,
  DeleteSandboxClassRequestSchema,
  DeleteSandboxClassResponseSchema,
  CreateSandboxInstanceFromClassRequestSchema,
  CreateSandboxInstanceFromClassResponseSchema,
  UpdateSandboxInstanceFromClassRequestSchema,
  UpdateSandboxInstanceFromClassResponseSchema,
} from '@/proto/agentplatform/httpv2/sandbox_pb';
import { type ListRequest, ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';

/** 列表返回的通用形状，兼容页面既有的 `data.data` / `data.total` 读法。 */
export interface SandboxClassListResult {
  data: SandboxClass[];
  total: number;
}

/** 由服务类派生实例时的入参（v2 已扁平化为 name/description/params）。 */
export interface SandboxInstanceFromClassInput {
  name: string;
  description: string;
  params: Record<string, string>;
}

export const sandboxClassApi = {
  listSandboxClasses: async (params: ListRequest): Promise<SandboxClassListResult> => {
    const req = create(ListRequestSchema, {
      page: params.page,
      pageSize: params.pageSize,
      filter: params.filter,
      fuzzy: params.fuzzy,
      orderBy: params.orderBy,
    });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/ListSandboxClasses',
      toJson(ListRequestSchema, req)
    );
    const resp = fromJson(ListSandboxClassesResponseSchema, responseObject.data);
    return { data: resp.data?.items ?? [], total: resp.data?.total ?? 0 };
  },

  getSandboxClass: async (id: string): Promise<SandboxClass> => {
    const req = create(GetSandboxClassRequestSchema, { id });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/GetSandboxClass',
      toJson(GetSandboxClassRequestSchema, req)
    );
    const resp = fromJson(GetSandboxClassResponseSchema, responseObject.data);
    return resp.data ?? create(SandboxClassSchema, {});
  },

  createSandboxClass: async (data: SandboxClass): Promise<SandboxClass> => {
    const req = create(CreateSandboxClassRequestSchema, { sandboxClass: data });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/CreateSandboxClass',
      toJson(CreateSandboxClassRequestSchema, req)
    );
    const resp = fromJson(CreateSandboxClassResponseSchema, responseObject.data);
    return resp.data ?? create(SandboxClassSchema, {});
  },

  updateSandboxClass: async (id: string, data: SandboxClass): Promise<SandboxClass> => {
    const req = create(UpdateSandboxClassRequestSchema, { id, sandboxClass: data });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/UpdateSandboxClass',
      toJson(UpdateSandboxClassRequestSchema, req)
    );
    const resp = fromJson(UpdateSandboxClassResponseSchema, responseObject.data);
    return resp.data ?? create(SandboxClassSchema, {});
  },

  deleteSandboxClass: async (id: string): Promise<void> => {
    const req = create(DeleteSandboxClassRequestSchema, { id });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/DeleteSandboxClass',
      toJson(DeleteSandboxClassRequestSchema, req)
    );
    fromJson(DeleteSandboxClassResponseSchema, responseObject.data);
  },

  createSandboxInstanceFromClass: async (
    classId: string,
    data: SandboxInstanceFromClassInput
  ): Promise<Sandbox> => {
    const req = create(CreateSandboxInstanceFromClassRequestSchema, {
      classId,
      name: data.name,
      description: data.description,
      params: data.params,
    });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/CreateSandboxInstanceFromClass',
      toJson(CreateSandboxInstanceFromClassRequestSchema, req)
    );
    const resp = fromJson(CreateSandboxInstanceFromClassResponseSchema, responseObject.data);
    return resp.data ?? create(SandboxSchema, {});
  },

  updateSandboxInstanceFromClass: async (
    id: string,
    classId: string,
    data: SandboxInstanceFromClassInput
  ): Promise<Sandbox> => {
    const req = create(UpdateSandboxInstanceFromClassRequestSchema, {
      id,
      classId,
      name: data.name,
      description: data.description,
      params: data.params,
    });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/UpdateSandboxInstanceFromClass',
      toJson(UpdateSandboxInstanceFromClassRequestSchema, req)
    );
    const resp = fromJson(UpdateSandboxInstanceFromClassResponseSchema, responseObject.data);
    return resp.data ?? create(SandboxSchema, {});
  },
};
